/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  GroupScore,
  InvestmentCalculation,
  InvestmentWalletAssociationRequest,
  InvestmentWalletSelectionRequest,
  InvestorCreation,
  InvestorCreationRequest,
  InvestorUpdateRequest,
  Invitation,
  InvitationOfferConfigurationRequest,
  InvitationSetQuestionnaireRequest,
  InvitationSetVibInfoRequest,
  InvitationTrackingRequest,
  KycInformation,
  ManagedAssetPreparation,
  PaginatedDropdownElementList,
  PaginatedInvitationList,
  QuestionnaireQuestion,
  SelfDisclosureCalculation,
  VibInfo,
  Voucher,
  WaiverRequest,
} from '../models/index';
import {
    GroupScoreFromJSON,
    GroupScoreToJSON,
    InvestmentCalculationFromJSON,
    InvestmentCalculationToJSON,
    InvestmentWalletAssociationRequestFromJSON,
    InvestmentWalletAssociationRequestToJSON,
    InvestmentWalletSelectionRequestFromJSON,
    InvestmentWalletSelectionRequestToJSON,
    InvestorCreationFromJSON,
    InvestorCreationToJSON,
    InvestorCreationRequestFromJSON,
    InvestorCreationRequestToJSON,
    InvestorUpdateRequestFromJSON,
    InvestorUpdateRequestToJSON,
    InvitationFromJSON,
    InvitationToJSON,
    InvitationOfferConfigurationRequestFromJSON,
    InvitationOfferConfigurationRequestToJSON,
    InvitationSetQuestionnaireRequestFromJSON,
    InvitationSetQuestionnaireRequestToJSON,
    InvitationSetVibInfoRequestFromJSON,
    InvitationSetVibInfoRequestToJSON,
    InvitationTrackingRequestFromJSON,
    InvitationTrackingRequestToJSON,
    KycInformationFromJSON,
    KycInformationToJSON,
    ManagedAssetPreparationFromJSON,
    ManagedAssetPreparationToJSON,
    PaginatedDropdownElementListFromJSON,
    PaginatedDropdownElementListToJSON,
    PaginatedInvitationListFromJSON,
    PaginatedInvitationListToJSON,
    QuestionnaireQuestionFromJSON,
    QuestionnaireQuestionToJSON,
    SelfDisclosureCalculationFromJSON,
    SelfDisclosureCalculationToJSON,
    VibInfoFromJSON,
    VibInfoToJSON,
    VoucherFromJSON,
    VoucherToJSON,
    WaiverRequestFromJSON,
    WaiverRequestToJSON,
} from '../models/index';

export interface InvitationsAbortionCreateRequest {
    id: string;
}

export interface InvitationsAssetPreparationDestroyRequest {
    id: string;
}

export interface InvitationsAssetPreparationRetrieveRequest {
    id: string;
}

export interface InvitationsAssetPreparationUpdateRequest {
    id: string;
}

export interface InvitationsCalculationsRetrieveRequest {
    id: string;
    code?: string;
    requestedTokens?: number;
}

export interface InvitationsDropdownListRequest {
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    distributionPlatformDisplayName?: string;
    field?: InvitationsDropdownListFieldEnum;
    hasInvestment?: boolean;
    id?: string;
    investment?: string;
    limit?: number;
    maxNumberOfTokensMax?: number;
    maxNumberOfTokensMin?: number;
    name?: string;
    offset?: number;
    ordering?: InvitationsDropdownListOrderingEnum;
    pricePerUnitMax?: number;
    pricePerUnitMin?: number;
    productName?: string;
    signedPercentageMax?: number;
    signedPercentageMin?: number;
}

export interface InvitationsIdentificationRetrieveRequest {
    id: string;
}

export interface InvitationsIdentificationStartNotificationCreateRequest {
    id: string;
}

export interface InvitationsListRequest {
    campaignId?: string;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    distributionPlatformDisplayName?: Array<string>;
    hasInvestment?: boolean;
    id?: Array<string>;
    investment?: Array<string>;
    invitationType?: string;
    isEcsp?: boolean;
    limit?: number;
    maxNumberOfTokensMax?: number;
    maxNumberOfTokensMin?: number;
    name?: Array<string>;
    offset?: number;
    ordering?: InvitationsListOrderingEnum;
    pricePerUnitMax?: number;
    pricePerUnitMin?: number;
    productName?: Array<string>;
    signedPercentageMax?: number;
    signedPercentageMin?: number;
    status?: string;
}

export interface InvitationsOfferConfigurationCreateRequest {
    id: string;
    invitationOfferConfigurationRequest: InvitationOfferConfigurationRequest;
}

export interface InvitationsOfferCreateRequest {
    id: string;
}

export interface InvitationsPrefilledQuestionnaireListRequest {
    id: string;
}

export interface InvitationsPrefilledVibInfoRetrieveRequest {
    id: string;
}

export interface InvitationsQuestionnaireCreateRequest {
    id: string;
    invitationSetQuestionnaireRequest: InvitationSetQuestionnaireRequest;
}

export interface InvitationsRegistrationCreateRequest {
    id: string;
    investorCreationRequest: InvestorCreationRequest;
}

export interface InvitationsRegistrationUpdateRequest {
    id: string;
    investorUpdateRequest?: InvestorUpdateRequest;
}

export interface InvitationsResetWalletSelectionCreateRequest {
    id: string;
}

export interface InvitationsRetrieveRequest {
    id: string;
}

export interface InvitationsSelfDisclosureRetrieveRequest {
    id: string;
    requestedTokens?: number;
}

export interface InvitationsSurveyScoreListRequest {
    id: string;
}

export interface InvitationsTrackingInformationCreateRequest {
    id: string;
    invitationTrackingRequest: InvitationTrackingRequest;
}

export interface InvitationsUserAssociationCreateRequest {
    id: string;
}

export interface InvitationsValuesRetrieveRequest {
    field: InvitationsValuesRetrieveFieldEnum;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    distributionPlatformDisplayName?: string;
    hasInvestment?: boolean;
    icontains?: string;
    id?: string;
    investment?: string;
    maxNumberOfTokensMax?: number;
    maxNumberOfTokensMin?: number;
    name?: string;
    ordering?: InvitationsValuesRetrieveOrderingEnum;
    pricePerUnitMax?: number;
    pricePerUnitMin?: number;
    productName?: string;
    signedPercentageMax?: number;
    signedPercentageMin?: number;
}

export interface InvitationsVibInfoCreateRequest {
    id: string;
    invitationSetVibInfoRequest: InvitationSetVibInfoRequest;
}

export interface InvitationsVoucherRetrieveRequest {
    id: string;
    code?: string;
    requestedTokens?: number;
}

export interface InvitationsWaiversCreateRequest {
    id: string;
    waiverRequest?: WaiverRequest;
}

export interface InvitationsWalletAssociationCreateRequest {
    id: string;
    investmentWalletAssociationRequest: InvestmentWalletAssociationRequest;
}

export interface InvitationsWalletSelectionCreateRequest {
    id: string;
    investmentWalletSelectionRequest: InvestmentWalletSelectionRequest;
}

/**
 * 
 */
export class InvitationsApi extends runtime.BaseAPI {

    /**
     * Abort an invitation.
     */
    async invitationsAbortionCreateRaw(requestParameters: InvitationsAbortionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsAbortionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/abortion/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Abort an invitation.
     */
    async invitationsAbortionCreate(requestParameters: InvitationsAbortionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsAbortionCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAssetPreparationDestroyRaw(requestParameters: InvitationsAssetPreparationDestroyRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsAssetPreparationDestroy().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/asset-preparation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'DELETE',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAssetPreparationDestroy(requestParameters: InvitationsAssetPreparationDestroyRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsAssetPreparationDestroyRaw(requestParameters, initOverrides);
    }

    /**
     * Get ManagedAssetPreparation
     */
    async invitationsAssetPreparationRetrieveRaw(requestParameters: InvitationsAssetPreparationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ManagedAssetPreparation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsAssetPreparationRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/asset-preparation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ManagedAssetPreparationFromJSON(jsonValue));
    }

    /**
     * Get ManagedAssetPreparation
     */
    async invitationsAssetPreparationRetrieve(requestParameters: InvitationsAssetPreparationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ManagedAssetPreparation> {
        const response = await this.invitationsAssetPreparationRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Create an Asset preparation
     */
    async invitationsAssetPreparationUpdateRaw(requestParameters: InvitationsAssetPreparationUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ManagedAssetPreparation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsAssetPreparationUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/asset-preparation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'PUT',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ManagedAssetPreparationFromJSON(jsonValue));
    }

    /**
     * Create an Asset preparation
     */
    async invitationsAssetPreparationUpdate(requestParameters: InvitationsAssetPreparationUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ManagedAssetPreparation> {
        const response = await this.invitationsAssetPreparationUpdateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Calculate some numbers for the frontend.
     */
    async invitationsCalculationsRetrieveRaw(requestParameters: InvitationsCalculationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestmentCalculation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsCalculationsRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['code'] != null) {
            queryParameters['code'] = requestParameters['code'];
        }

        if (requestParameters['requestedTokens'] != null) {
            queryParameters['requested_tokens'] = requestParameters['requestedTokens'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/calculations/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestmentCalculationFromJSON(jsonValue));
    }

    /**
     * Calculate some numbers for the frontend.
     */
    async invitationsCalculationsRetrieve(requestParameters: InvitationsCalculationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestmentCalculation> {
        const response = await this.invitationsCalculationsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async invitationsDropdownListRaw(requestParameters: InvitationsDropdownListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDropdownElementList>> {
        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['distributionPlatformDisplayName'] != null) {
            queryParameters['distribution_platform_display_name'] = requestParameters['distributionPlatformDisplayName'];
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['hasInvestment'] != null) {
            queryParameters['has_investment'] = requestParameters['hasInvestment'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investment'] != null) {
            queryParameters['investment'] = requestParameters['investment'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['maxNumberOfTokensMax'] != null) {
            queryParameters['max_number_of_tokens_max'] = requestParameters['maxNumberOfTokensMax'];
        }

        if (requestParameters['maxNumberOfTokensMin'] != null) {
            queryParameters['max_number_of_tokens_min'] = requestParameters['maxNumberOfTokensMin'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['pricePerUnitMax'] != null) {
            queryParameters['price_per_unit_max'] = requestParameters['pricePerUnitMax'];
        }

        if (requestParameters['pricePerUnitMin'] != null) {
            queryParameters['price_per_unit_min'] = requestParameters['pricePerUnitMin'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['signedPercentageMax'] != null) {
            queryParameters['signed_percentage_max'] = requestParameters['signedPercentageMax'];
        }

        if (requestParameters['signedPercentageMin'] != null) {
            queryParameters['signed_percentage_min'] = requestParameters['signedPercentageMin'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/dropdown/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDropdownElementListFromJSON(jsonValue));
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async invitationsDropdownList(requestParameters: InvitationsDropdownListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDropdownElementList> {
        const response = await this.invitationsDropdownListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Retrieve an identification. Create it if it does not exists.
     */
    async invitationsIdentificationRetrieveRaw(requestParameters: InvitationsIdentificationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<KycInformation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsIdentificationRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/identification/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => KycInformationFromJSON(jsonValue));
    }

    /**
     * Retrieve an identification. Create it if it does not exists.
     */
    async invitationsIdentificationRetrieve(requestParameters: InvitationsIdentificationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<KycInformation> {
        const response = await this.invitationsIdentificationRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Set the KYC status of an identification to \'started\'.
     */
    async invitationsIdentificationStartNotificationCreateRaw(requestParameters: InvitationsIdentificationStartNotificationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsIdentificationStartNotificationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/identification-start-notification/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Set the KYC status of an identification to \'started\'.
     */
    async invitationsIdentificationStartNotificationCreate(requestParameters: InvitationsIdentificationStartNotificationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsIdentificationStartNotificationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * List Invitations.
     */
    async invitationsListRaw(requestParameters: InvitationsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedInvitationList>> {
        const queryParameters: any = {};

        if (requestParameters['campaignId'] != null) {
            queryParameters['campaign_id'] = requestParameters['campaignId'];
        }

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['distributionPlatformDisplayName'] != null) {
            queryParameters['distribution_platform_display_name'] = requestParameters['distributionPlatformDisplayName'];
        }

        if (requestParameters['hasInvestment'] != null) {
            queryParameters['has_investment'] = requestParameters['hasInvestment'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investment'] != null) {
            queryParameters['investment'] = requestParameters['investment'];
        }

        if (requestParameters['invitationType'] != null) {
            queryParameters['invitation_type'] = requestParameters['invitationType'];
        }

        if (requestParameters['isEcsp'] != null) {
            queryParameters['is_ecsp'] = requestParameters['isEcsp'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['maxNumberOfTokensMax'] != null) {
            queryParameters['max_number_of_tokens_max'] = requestParameters['maxNumberOfTokensMax'];
        }

        if (requestParameters['maxNumberOfTokensMin'] != null) {
            queryParameters['max_number_of_tokens_min'] = requestParameters['maxNumberOfTokensMin'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['pricePerUnitMax'] != null) {
            queryParameters['price_per_unit_max'] = requestParameters['pricePerUnitMax'];
        }

        if (requestParameters['pricePerUnitMin'] != null) {
            queryParameters['price_per_unit_min'] = requestParameters['pricePerUnitMin'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['signedPercentageMax'] != null) {
            queryParameters['signed_percentage_max'] = requestParameters['signedPercentageMax'];
        }

        if (requestParameters['signedPercentageMin'] != null) {
            queryParameters['signed_percentage_min'] = requestParameters['signedPercentageMin'];
        }

        if (requestParameters['status'] != null) {
            queryParameters['status'] = requestParameters['status'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedInvitationListFromJSON(jsonValue));
    }

    /**
     * List Invitations.
     */
    async invitationsList(requestParameters: InvitationsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedInvitationList> {
        const response = await this.invitationsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsOfferConfigurationCreateRaw(requestParameters: InvitationsOfferConfigurationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsOfferConfigurationCreate().'
            );
        }

        if (requestParameters['invitationOfferConfigurationRequest'] == null) {
            throw new runtime.RequiredError(
                'invitationOfferConfigurationRequest',
                'Required parameter "invitationOfferConfigurationRequest" was null or undefined when calling invitationsOfferConfigurationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/offer-configuration/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvitationOfferConfigurationRequestToJSON(requestParameters['invitationOfferConfigurationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsOfferConfigurationCreate(requestParameters: InvitationsOfferConfigurationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsOfferConfigurationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Create an offer.
     */
    async invitationsOfferCreateRaw(requestParameters: InvitationsOfferCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsOfferCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/offer/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Create an offer.
     */
    async invitationsOfferCreate(requestParameters: InvitationsOfferCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsOfferCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsPrefilledQuestionnaireListRaw(requestParameters: InvitationsPrefilledQuestionnaireListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<QuestionnaireQuestion>>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsPrefilledQuestionnaireList().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/prefilled-questionnaire/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(QuestionnaireQuestionFromJSON));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsPrefilledQuestionnaireList(requestParameters: InvitationsPrefilledQuestionnaireListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<QuestionnaireQuestion>> {
        const response = await this.invitationsPrefilledQuestionnaireListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsPrefilledVibInfoRetrieveRaw(requestParameters: InvitationsPrefilledVibInfoRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<VibInfo>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsPrefilledVibInfoRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/prefilled-vib-info/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => VibInfoFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsPrefilledVibInfoRetrieve(requestParameters: InvitationsPrefilledVibInfoRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<VibInfo> {
        const response = await this.invitationsPrefilledVibInfoRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsQuestionnaireCreateRaw(requestParameters: InvitationsQuestionnaireCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Invitation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsQuestionnaireCreate().'
            );
        }

        if (requestParameters['invitationSetQuestionnaireRequest'] == null) {
            throw new runtime.RequiredError(
                'invitationSetQuestionnaireRequest',
                'Required parameter "invitationSetQuestionnaireRequest" was null or undefined when calling invitationsQuestionnaireCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/questionnaire/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvitationSetQuestionnaireRequestToJSON(requestParameters['invitationSetQuestionnaireRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvitationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsQuestionnaireCreate(requestParameters: InvitationsQuestionnaireCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Invitation> {
        const response = await this.invitationsQuestionnaireCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Create the investor.
     */
    async invitationsRegistrationCreateRaw(requestParameters: InvitationsRegistrationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestorCreation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsRegistrationCreate().'
            );
        }

        if (requestParameters['investorCreationRequest'] == null) {
            throw new runtime.RequiredError(
                'investorCreationRequest',
                'Required parameter "investorCreationRequest" was null or undefined when calling invitationsRegistrationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/registration/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestorCreationRequestToJSON(requestParameters['investorCreationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestorCreationFromJSON(jsonValue));
    }

    /**
     * Create the investor.
     */
    async invitationsRegistrationCreate(requestParameters: InvitationsRegistrationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestorCreation> {
        const response = await this.invitationsRegistrationCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Update the registration.
     */
    async invitationsRegistrationUpdateRaw(requestParameters: InvitationsRegistrationUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsRegistrationUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/registration/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'PUT',
            headers: headerParameters,
            query: queryParameters,
            body: InvestorUpdateRequestToJSON(requestParameters['investorUpdateRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Update the registration.
     */
    async invitationsRegistrationUpdate(requestParameters: InvitationsRegistrationUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsRegistrationUpdateRaw(requestParameters, initOverrides);
    }

    /**
     * If before setting up a wallet, an investor decides to choose another wallet type, then this endpoint needs to be called to reset the `add_wallet_later` field.  If a wallet was already attached but the trustline / signing flow has not yet succeeded (no SUCCEEDED ProductWalletAssignment), the wallet is also detached and the in-progress PWA is dropped so the wizard recomputes back to SELECT_WALLET.
     */
    async invitationsResetWalletSelectionCreateRaw(requestParameters: InvitationsResetWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsResetWalletSelectionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/reset-wallet-selection/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * If before setting up a wallet, an investor decides to choose another wallet type, then this endpoint needs to be called to reset the `add_wallet_later` field.  If a wallet was already attached but the trustline / signing flow has not yet succeeded (no SUCCEEDED ProductWalletAssignment), the wallet is also detached and the in-progress PWA is dropped so the wizard recomputes back to SELECT_WALLET.
     */
    async invitationsResetWalletSelectionCreate(requestParameters: InvitationsResetWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsResetWalletSelectionCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Retrieve an Invitation.
     */
    async invitationsRetrieveRaw(requestParameters: InvitationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Invitation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvitationFromJSON(jsonValue));
    }

    /**
     * Retrieve an Invitation.
     */
    async invitationsRetrieve(requestParameters: InvitationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Invitation> {
        const response = await this.invitationsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsSelfDisclosureRetrieveRaw(requestParameters: InvitationsSelfDisclosureRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<SelfDisclosureCalculation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsSelfDisclosureRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['requestedTokens'] != null) {
            queryParameters['requested_tokens'] = requestParameters['requestedTokens'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/self-disclosure/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => SelfDisclosureCalculationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsSelfDisclosureRetrieve(requestParameters: InvitationsSelfDisclosureRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<SelfDisclosureCalculation> {
        const response = await this.invitationsSelfDisclosureRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Determines if the survey submitted for an invitation has the related score above the minimum score for each of the survey groups
     */
    async invitationsSurveyScoreListRaw(requestParameters: InvitationsSurveyScoreListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<GroupScore>>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsSurveyScoreList().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/survey-score/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(GroupScoreFromJSON));
    }

    /**
     * Determines if the survey submitted for an invitation has the related score above the minimum score for each of the survey groups
     */
    async invitationsSurveyScoreList(requestParameters: InvitationsSurveyScoreListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<GroupScore>> {
        const response = await this.invitationsSurveyScoreListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsTrackingInformationCreateRaw(requestParameters: InvitationsTrackingInformationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsTrackingInformationCreate().'
            );
        }

        if (requestParameters['invitationTrackingRequest'] == null) {
            throw new runtime.RequiredError(
                'invitationTrackingRequest',
                'Required parameter "invitationTrackingRequest" was null or undefined when calling invitationsTrackingInformationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/tracking-information/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvitationTrackingRequestToJSON(requestParameters['invitationTrackingRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsTrackingInformationCreate(requestParameters: InvitationsTrackingInformationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsTrackingInformationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * especially needed for campaign invitations which cannot be associated with a user automatically due to lack of an email address.
     */
    async invitationsUserAssociationCreateRaw(requestParameters: InvitationsUserAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsUserAssociationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/user-association/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * especially needed for campaign invitations which cannot be associated with a user automatically due to lack of an email address.
     */
    async invitationsUserAssociationCreate(requestParameters: InvitationsUserAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsUserAssociationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async invitationsValuesRetrieveRaw(requestParameters: InvitationsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<string>>> {
        if (requestParameters['field'] == null) {
            throw new runtime.RequiredError(
                'field',
                'Required parameter "field" was null or undefined when calling invitationsValuesRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['distributionPlatformDisplayName'] != null) {
            queryParameters['distribution_platform_display_name'] = requestParameters['distributionPlatformDisplayName'];
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['hasInvestment'] != null) {
            queryParameters['has_investment'] = requestParameters['hasInvestment'];
        }

        if (requestParameters['icontains'] != null) {
            queryParameters['icontains'] = requestParameters['icontains'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['investment'] != null) {
            queryParameters['investment'] = requestParameters['investment'];
        }

        if (requestParameters['maxNumberOfTokensMax'] != null) {
            queryParameters['max_number_of_tokens_max'] = requestParameters['maxNumberOfTokensMax'];
        }

        if (requestParameters['maxNumberOfTokensMin'] != null) {
            queryParameters['max_number_of_tokens_min'] = requestParameters['maxNumberOfTokensMin'];
        }

        if (requestParameters['name'] != null) {
            queryParameters['name'] = requestParameters['name'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['pricePerUnitMax'] != null) {
            queryParameters['price_per_unit_max'] = requestParameters['pricePerUnitMax'];
        }

        if (requestParameters['pricePerUnitMin'] != null) {
            queryParameters['price_per_unit_min'] = requestParameters['pricePerUnitMin'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['signedPercentageMax'] != null) {
            queryParameters['signed_percentage_max'] = requestParameters['signedPercentageMax'];
        }

        if (requestParameters['signedPercentageMin'] != null) {
            queryParameters['signed_percentage_min'] = requestParameters['signedPercentageMin'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/values/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async invitationsValuesRetrieve(requestParameters: InvitationsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<string>> {
        const response = await this.invitationsValuesRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsVibInfoCreateRaw(requestParameters: InvitationsVibInfoCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsVibInfoCreate().'
            );
        }

        if (requestParameters['invitationSetVibInfoRequest'] == null) {
            throw new runtime.RequiredError(
                'invitationSetVibInfoRequest',
                'Required parameter "invitationSetVibInfoRequest" was null or undefined when calling invitationsVibInfoCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/vib-info/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvitationSetVibInfoRequestToJSON(requestParameters['invitationSetVibInfoRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsVibInfoCreate(requestParameters: InvitationsVibInfoCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsVibInfoCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Get the invitations voucher.
     */
    async invitationsVoucherRetrieveRaw(requestParameters: InvitationsVoucherRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Voucher>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsVoucherRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['code'] != null) {
            queryParameters['code'] = requestParameters['code'];
        }

        if (requestParameters['requestedTokens'] != null) {
            queryParameters['requested_tokens'] = requestParameters['requestedTokens'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/invitations/{id}/voucher/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => VoucherFromJSON(jsonValue));
    }

    /**
     * Get the invitations voucher.
     */
    async invitationsVoucherRetrieve(requestParameters: InvitationsVoucherRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Voucher> {
        const response = await this.invitationsVoucherRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsWaiversCreateRaw(requestParameters: InvitationsWaiversCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsWaiversCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/waivers/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: WaiverRequestToJSON(requestParameters['waiverRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsWaiversCreate(requestParameters: InvitationsWaiversCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsWaiversCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsWalletAssociationCreateRaw(requestParameters: InvitationsWalletAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsWalletAssociationCreate().'
            );
        }

        if (requestParameters['investmentWalletAssociationRequest'] == null) {
            throw new runtime.RequiredError(
                'investmentWalletAssociationRequest',
                'Required parameter "investmentWalletAssociationRequest" was null or undefined when calling invitationsWalletAssociationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/wallet-association/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestmentWalletAssociationRequestToJSON(requestParameters['investmentWalletAssociationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsWalletAssociationCreate(requestParameters: InvitationsWalletAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsWalletAssociationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Select a wallet type without creating a wallet immediately. Used for mobile wallet onboarding.
     */
    async invitationsWalletSelectionCreateRaw(requestParameters: InvitationsWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsWalletSelectionCreate().'
            );
        }

        if (requestParameters['investmentWalletSelectionRequest'] == null) {
            throw new runtime.RequiredError(
                'investmentWalletSelectionRequest',
                'Required parameter "investmentWalletSelectionRequest" was null or undefined when calling invitationsWalletSelectionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/invitations/{id}/wallet-selection/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestmentWalletSelectionRequestToJSON(requestParameters['investmentWalletSelectionRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Select a wallet type without creating a wallet immediately. Used for mobile wallet onboarding.
     */
    async invitationsWalletSelectionCreate(requestParameters: InvitationsWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsWalletSelectionCreateRaw(requestParameters, initOverrides);
    }

}

/**
 * @export
 */
export const InvitationsDropdownListFieldEnum = {
    DISTRIBUTION_PLATFORM_DISPLAY_NAME: 'distribution_platform_display_name',
    NAME: 'name',
    PRODUCT_NAME: 'product_name'
} as const;
export type InvitationsDropdownListFieldEnum = typeof InvitationsDropdownListFieldEnum[keyof typeof InvitationsDropdownListFieldEnum];
/**
 * @export
 */
export const InvitationsDropdownListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    UPDATED_AT_DESC: '-updated_at',
    UPDATED_AT_ASC: 'updated_at'
} as const;
export type InvitationsDropdownListOrderingEnum = typeof InvitationsDropdownListOrderingEnum[keyof typeof InvitationsDropdownListOrderingEnum];
/**
 * @export
 */
export const InvitationsListOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    UPDATED_AT_DESC: '-updated_at',
    UPDATED_AT_ASC: 'updated_at'
} as const;
export type InvitationsListOrderingEnum = typeof InvitationsListOrderingEnum[keyof typeof InvitationsListOrderingEnum];
/**
 * @export
 */
export const InvitationsValuesRetrieveFieldEnum = {
    DISTRIBUTION_PLATFORM_DISPLAY_NAME: 'distribution_platform_display_name',
    NAME: 'name',
    PRODUCT_NAME: 'product_name'
} as const;
export type InvitationsValuesRetrieveFieldEnum = typeof InvitationsValuesRetrieveFieldEnum[keyof typeof InvitationsValuesRetrieveFieldEnum];
/**
 * @export
 */
export const InvitationsValuesRetrieveOrderingEnum = {
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    UPDATED_AT_DESC: '-updated_at',
    UPDATED_AT_ASC: 'updated_at'
} as const;
export type InvitationsValuesRetrieveOrderingEnum = typeof InvitationsValuesRetrieveOrderingEnum[keyof typeof InvitationsValuesRetrieveOrderingEnum];
